import { updateSal } from "./sal-init";

const STORAGE_KEY = "modulo-projects-view";

function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

function setActiveView(viewName) {
  const toggles = document.querySelectorAll(".js-projects-view-toggle");
  const views = document.querySelectorAll(".js-projects-view");

  toggles.forEach((toggle) => {
    const isActive = toggle.getAttribute("data-view") === viewName;
    toggle.classList.toggle("is-active", isActive);
    toggle.setAttribute("aria-pressed", isActive ? "true" : "false");
  });

  views.forEach((view) => {
    view.classList.toggle("u-hidden", view.getAttribute("data-view") !== viewName);
  });

  document.body.setAttribute("data-current-view", viewName);

  try {
    sessionStorage.setItem(STORAGE_KEY, viewName);
  } catch (error) {
    // Ignore storage errors.
  }

  if (viewName !== "index") {
    // Skip during Swup enter — visit:end owns the first initSal().
    if (document.documentElement.classList.contains("is-changing")) {
      return;
    }

    // Wait until the view is painted (u-hidden removed) before observing.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateSal();
      });
    });
  }
}

function getStoredView() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch (error) {
    return null;
  }
}

function updateFilterDisplay(items, activeFilter) {
  items.forEach((item) => {
    if (item.hasAttribute("data-filter-always")) {
      item.classList.remove("is-hidden");
      return;
    }

    const typology = item.getAttribute("data-typology") || "";

    if (!activeFilter) {
      item.classList.remove("is-hidden");
      return;
    }

    const typologyValues = typology
      .split(",")
      .map((value) => slugify(value.trim()))
      .filter(Boolean);

    const matchesFilter = typologyValues.includes(activeFilter);
    item.classList.toggle("is-hidden", !matchesFilter);
  });
}

let projectsViewAbort = null;

function initProjectsFilters(root = document) {
  const filters = root.querySelector(".js-projects-filters");
  const toggle = root.querySelector(".js-projects-filters-toggle");

  if (!filters || !toggle) {
    return;
  }

  projectsViewAbort?.abort();
  projectsViewAbort = new AbortController();
  const { signal } = projectsViewAbort;

  const setOpen = (isOpen) => {
    filters.setAttribute("data-state", isOpen ? "open" : "closed");
    toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
  };

  toggle.addEventListener(
    "click",
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(filters.getAttribute("data-state") === "closed");
    },
    { signal }
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!filters.contains(event.target)) {
        setOpen(false);
      }
    },
    { signal }
  );
}

function initProjectsView(root = document) {
  const toggles = root.querySelectorAll(".js-projects-view-toggle");
  const views = root.querySelectorAll(".js-projects-view");
  const filterLinks = root.querySelectorAll(".c-projects-filters .js-filter-link");
  const filterItems = root.querySelectorAll(".js-item-to-filter");

  if (toggles.length === 0 || views.length === 0) {
    return;
  }

  const storedView = getStoredView();
  const selectedView = root.querySelector('.js-projects-view[data-view="selected"]');
  const selectedHasRows = Boolean(
    selectedView &&
      selectedView.querySelector(
        ".c-projects-selected__row, .c-projects-selected__item"
      )
  );
  let initialView = storedView === "index" ? "index" : "selected";

  // Avoid a blank page when the selected gallery has no rows (e.g. missing
  // default-language images) — fall back to the index list.
  if (initialView === "selected" && !selectedHasRows) {
    initialView = "index";
  }

  setActiveView(initialView);

  initProjectsFilters(root);

  toggles.forEach((toggle) => {
    toggle.addEventListener("click", (event) => {
      event.preventDefault();
      setActiveView(toggle.getAttribute("data-view"));
    });
  });

  if (filterLinks.length === 0 || filterItems.length === 0) {
    return;
  }

  let activeFilter = "";

  filterLinks.forEach((link) => {
    if (link.classList.contains("is-active")) {
      activeFilter = link.getAttribute("data-filter") || "";
    }

    link.addEventListener("click", (event) => {
      event.preventDefault();

      const filterValue = link.getAttribute("data-filter") || "";
      activeFilter = filterValue;

      filterLinks.forEach((button) => {
        button.classList.toggle("is-active", button === link);
      });

      updateFilterDisplay(filterItems, activeFilter);
    });
  });

  const urlParams = new URLSearchParams(window.location.search);
  const categoryParam = urlParams.get("categories");

  if (categoryParam) {
    const matchingLink = Array.from(filterLinks).find((link) => {
      return link.getAttribute("data-filter") === categoryParam;
    });

    if (matchingLink) {
      filterLinks.forEach((button) => {
        button.classList.remove("is-active");
      });
      matchingLink.classList.add("is-active");
      activeFilter = categoryParam;
      updateFilterDisplay(filterItems, activeFilter);
    }
  } else {
    updateFilterDisplay(filterItems, activeFilter);
  }
}

export { initProjectsView, setActiveView as setProjectsView };
